import { SchemaAttributes, SchemaGroups } from '@/Data/typesV2'

export interface VisibleAttributesResult {
  /** Flat array of visible attribute names in order */
  visibleAttributes: string[]
  /** Map of attribute name to group name */
  attributeGroupMap: Map<string, string>
}

/**
 * Gets visible attributes based on schema group attributeOrder arrays.
 * This ensures consistency between forms, filters, and other UI components.
 *
 * @param schemaGroups - The schema groups containing attributeOrder arrays
 * @param groupsOrder - The ordered list of group names
 * @param schemaAttributes - The full schema attributes object (accepts both old and new type formats)
 * @returns Object containing flat array of visible attributes and attribute-to-group mapping
 */
export function getVisibleAttributes(
  schemaGroups: SchemaGroups,
  groupsOrder: string[] | undefined,
  schemaAttributes: SchemaAttributes | Record<string, unknown>,
): VisibleAttributesResult {
  const visibleAttributes: string[] = []
  const attributeGroupMap = new Map<string, string>()

  // Guard against undefined or null values
  if (!groupsOrder || !schemaGroups || !schemaAttributes) {
    return { visibleAttributes, attributeGroupMap }
  }

  groupsOrder.forEach((groupName) => {
    const group = schemaGroups[groupName]
    if (!group?.attributeOrder) return

    group.attributeOrder.forEach((attrName) => {
      // Only include if attribute actually exists in schemaAttributes
      if (schemaAttributes[attrName]) {
        visibleAttributes.push(attrName)
        attributeGroupMap.set(attrName, groupName)
      }
    })
  })

  return { visibleAttributes, attributeGroupMap }
}

/**
 * Checks if an attribute is visible in the schema.
 * An attribute is visible if it appears in any group's attributeOrder array.
 *
 * @param attributeName - The name of the attribute to check
 * @param schemaGroups - The schema groups containing attributeOrder arrays
 * @param groupsOrder - The ordered list of group names
 * @returns true if the attribute is visible, false otherwise
 */
export function isAttributeVisible(
  attributeName: string,
  schemaGroups: SchemaGroups,
  groupsOrder: string[] | undefined,
): boolean {
  // Guard against undefined or null values
  if (!groupsOrder || !schemaGroups) {
    return false
  }

  return groupsOrder.some((groupName) => {
    const group = schemaGroups[groupName]
    return group?.attributeOrder?.includes(attributeName) || false
  })
}
